// E-signature field vocabulary + geometry validation (Prepare & Send).
//
// CONSUMER: shared (Workers runtime + web via @shared + tests).
//
// Provider-neutral: the OS stores and transmits fields in ITS OWN
// vocabulary and coordinate space, and exactly one function maps them
// to BoldSign's send shape. Coordinate space, stated once and used
// everywhere: PDF points (1/72 inch), TOP-LEFT origin, y increasing
// DOWNWARD, page numbers 1-based — i.e. the pdf.js viewport space at
// scale 1, which is what the prepare screen's overlay works in. This
// is NOT raw PDF user space (bottom-left, y-up); pdf.js's viewport
// applies that flip for us on the client.
//
// Pure and dependency-free (mirrors shared/filters/field-registry.ts's
// rule) so the web bundle and node-pool tests import it without
// dragging in DB or fetch code. The imports below are sibling modules
// that follow the same rule (and one is type-only and erases at build
// time), so it still holds.

import type { FieldAnchor } from "./layout-anchors";
import { isMultilineField, MAX_LINE_PITCH_PT, MIN_LINE_PITCH_PT } from "./multiline-text";
import { parseTextStyle, type EsignTextStyle } from "./text-style";
import { isKnownRoleKey } from "./signing-roles";

/** The field types the prepare screen offers. Kept in lockstep with the
 *  CHECK constraint in migration 20260824230106. */
export type EsignFieldType =
  | "signature"
  | "initials"
  | "date_signed"
  | "text"
  | "checkbox"
  | "dropdown"
  | "radio"
  | "editable_date"
  | "attachment";

export const ESIGN_FIELD_TYPES: readonly EsignFieldType[] = [
  "signature",
  "initials",
  "date_signed",
  "text",
  "checkbox",
  "dropdown",
  "radio",
  "editable_date",
  "attachment",
];

// ── A BOX THE PREPARER FILLS AND THE SIGNER CANNOT TOUCH ───────────────
//
// Baker, 2026-09-14: "I need to be able to put in data specific to this
// loan that is NOT found in the OS, but needs to be in this document...
// When I put in a text box, that was assigned to signer 1 and was
// prefilled with data, when I opened the document to sign, that box was
// editable for me the signer. Joey will need the ability to put in data
// that CAN NOT be edited by a signer."
//
// He is right that it could not be done. Every field belongs to a
// signer, and a field belonging to a signer is handed to the signing
// service as THEIR form field — a prefilled value is a default, not a
// fact, and of course they can type over it.
//
// So a preparer's box is deliberately NOT a signing-service field at
// all. It takes the path the advisor's own signature already takes: it
// is drawn into the PDF's bytes before the document is ever uploaded,
// which is the only way "uneditable" is actually true rather than
// merely asked for politely. Everything else about it — placing it,
// dragging it, binding it to a record value, remembering it in a saved
// map — reuses the ordinary field machinery, which is why it is a
// SIGNER KEY rather than a parallel concept.
//
// The one rule that must never break: a field owned by this key must
// never reach the provider. That exclusion happens in exactly one place
// (the send route's payload build) and is asserted by its own tests.
export const PREPARER_SIGNER_KEY = "__preparer__";

/** Is this field the preparer's own, rather than a signer's? */
export function isPreparerField(f: { signerKey: string }): boolean {
  return f.signerKey === PREPARER_SIGNER_KEY;
}

/** The field types a preparer may lock.
 *
 *  Text, and a checkbox (2026-09-18). NOT a signature: a locked
 *  signature is the advisor's own stamp, which already exists and
 *  carries an audit trail this deliberately does not duplicate.
 *
 *  The checkbox was missing and it is the commonest locked thing there
 *  is. Measured on the compiled Realty atlas: of 1,556 preparer-owned
 *  blanks, 866 are checkboxes — the agency election, which box the
 *  earnest money goes in, every "Addenda Incorporated" tick — and every
 *  one was refused at send with "One of the placed fields has an unknown
 *  type". The atlas import was producing layouts this rule structurally
 *  rejected, on almost every form. What a ticked one prints, and why an
 *  unticked one prints nothing, is in preparer-stamps.ts. */
export const PREPARER_FIELD_TYPES: readonly EsignFieldType[] = ["text", "checkbox"];

// ── A VALUE WE FILLED IN IS A FACT, NOT A SUGGESTION ───────────────────
//
// Brian Baker, 2026-09-18, signing a real StewDocs package end to end:
// "This document is still editable by me the signer." Twice, on two
// different documents, and then: "these fields just look off... it's
// like the boxes don't match up or are too big for the lines they are
// on. looks sloppy. Could be because its editable."
//
// He was right about the cause, and it is the same cause as the locked
// box above — one step further out. A locked box solved it for a value
// the PREPARER types. It did nothing for the far commoner case: a value
// the OS itself fills in from the record, or one the sender types into
// an ordinary text field. Those are signer fields, and a value on a
// signer's field is handed to the provider as THEIR default, which is
// why Baker could type over his own name, the property address, the
// loan number, the rate and the fee.
//
// The provider has a flag for exactly this, and we never set it. So the
// rule is now: A TEXT FIELD THAT ARRIVES WITH A VALUE IS PRINTED, NOT
// TYPED. The signer reads it; there is nothing to click.
//
// TEXT ONLY, and the exclusions are the point:
//
//   * A CHECKBOX, DROPDOWN or RADIO is an ELECTION. Prefilling one is
//     offering an answer; locking it would be making the client's
//     choice for them, which is a different and much worse bug than the
//     one being fixed.
//   * An EDITABLE DATE is by definition a date the signer picks — a
//     value on it is a starting point, which is what that field type
//     exists to be.
//   * A DATE SIGNED is stamped by the provider at the moment of
//     signing and carries no value of ours at all.
//
// The escape hatch is per-field and deliberate: `signerMayEdit` keeps a
// prefill editable for the rare blank where the sender means it as a
// head start ("Other: ____"). It is opt-IN, so forgetting it leaves the
// value protected rather than exposed.

/** Would this field's value be PRINTED onto the page for the signer to
 *  read, rather than offered to them as an editable default?
 *
 *  Answered against the field as it stands at SEND time, after record
 *  bindings have resolved into `value` — a bound field is empty on the
 *  wire and full by the time it reaches the provider, so asking any
 *  earlier gets the wrong answer for exactly the fields Baker hit. */
export function printsAsReadOnly(f: {
  signerKey: string;
  fieldType: EsignFieldType;
  value?: string | null;
  signerMayEdit?: boolean | null;
}): boolean {
  if (isPreparerField(f)) return false; // never reaches the provider at all
  if (f.fieldType !== "text") return false;
  if (f.signerMayEdit === true) return false;
  return typeof f.value === "string" && f.value.trim().length > 0;
}

/** Does this field's value get DRAWN INTO THE PDF by the OS rather than
 *  travelling to the provider as a form field?
 *
 *  Two cases, and they print by the same mechanism (preparer-stamps.ts →
 *  document-stamping.ts): the preparer's own locked box, which has always
 *  been drawn in, and — since 2026-09-29 — a MULTI-LINE text value that
 *  prints read-only. The provider lays multi-line text out on its own
 *  spacing, not on the form's printed rules, and shrinks a read-only
 *  box onto one line; the OS knows exactly where every rule is, so it
 *  prints the lines itself. A multi-line box the signer TYPES INTO
 *  still goes to the provider as a tall text box. */
export function printsByStamp(f: {
  signerKey: string;
  fieldType: EsignFieldType;
  value?: string | null;
  signerMayEdit?: boolean | null;
  multiline?: boolean | null;
  linePitch?: number | null;
}): boolean {
  if (isPreparerField(f)) return true;
  return isMultilineField(f) && printsAsReadOnly(f);
}

/** The multi-line facts a request carries, validated, or nothing at all
 *  (never the keys set to null — `exactOptionalPropertyTypes` treats a
 *  missing key and an explicit null differently, and "nobody said" is
 *  the missing one). Only a text field can carry them. */
function multilineFacts(f: Record<string, unknown>): {
  multiline?: boolean;
  linePitch?: number;
  firstLineIndent?: number;
} {
  if (f.fieldType !== "text" || f.multiline !== true) return {};
  const pitch = f.linePitch;
  if (
    typeof pitch !== "number" ||
    !Number.isFinite(pitch) ||
    pitch < MIN_LINE_PITCH_PT ||
    pitch > MAX_LINE_PITCH_PT
  ) {
    return {};
  }
  const width = typeof f.width === "number" ? f.width : 0;
  const indent = f.firstLineIndent;
  const validIndent =
    typeof indent === "number" && Number.isFinite(indent) && indent > 0 && indent < width
      ? indent
      : null;
  return {
    multiline: true,
    linePitch: pitch,
    ...(validIndent !== null ? { firstLineIndent: validIndent } : {}),
  };
}

/** What a printed value is set in, when the OS could not read the size
 *  of the type around it.
 *
 *  A JUDGMENT, not a measurement, and worth naming as one: the
 *  disclosure and contract forms this firm sends run roughly 9 to 11
 *  point, so a value printed at 10 sits among them without announcing
 *  itself. The honest answer is `fontSize` carried from the caption
 *  beside the blank (see vector-blanks.ts), which is what every newly
 *  read document now supplies; this is the floor under a saved map from
 *  before that existed, and it is still far closer than the provider's
 *  own default, which is sized for a web form. */
export const DEFAULT_PRINTED_FONT_PT = 10;

/** Type sizes outside this are a mis-read, not a small-print form. */
export const MIN_FIELD_FONT_PT = 5;
export const MAX_FIELD_FONT_PT = 36;

/** How tall a box has to be to hold one line of type comfortably.
 *  Leading, near enough: a 10pt value wants about 13pt of room. */
export const LINE_BOX_RATIO = 1.35;

/** The size a field's value prints at: what the document itself told us
 *  when the blank was mapped, or the firm's forms' own body size when
 *  nothing did. Never the provider's default, which is the thing being
 *  corrected. */
export function printedFontSize(f: { fontSize?: number | null }): number {
  return typeof f.fontSize === "number" && f.fontSize > 0 ? f.fontSize : DEFAULT_PRINTED_FONT_PT;
}

/** Where a PRINTED value actually sits, given the box the map stored.
 *
 *  The second half of Baker's "too big for the lines they are on", and
 *  the half that a saved map cannot fix on its own. Until 2026-09-18
 *  every text box proposed from a ruled line was a flat 24 points tall,
 *  seated so its BOTTOM edge rests on the rule. That is right for a box
 *  somebody taps into — it is a target — and wrong for a value that is
 *  merely printed, because the provider sets the text at the top of the
 *  box it is given, leaving it hanging most of a centimetre above the
 *  line it belongs to.
 *
 *  So a printed value gets a box only as tall as its own type, and the
 *  BOTTOM EDGE IS THE ONE THAT DOES NOT MOVE — that edge is the rule
 *  the form drew, and the whole point is to sit on it.
 *
 *  Only ever shrinks, which is what makes it safe to apply to a map
 *  saved years ago: a box that is already tight is returned untouched,
 *  and a shorter box inside an on-page box is still on the page, so no
 *  send that validated before can fail this. An editable box is never
 *  passed through here at all — shrinking a tap target is its own bug,
 *  and a 13-point box on a phone is not something a person can hit. */
export function printedValueBox(
  box: { x: number; y: number; width: number; height: number },
  fontSizePt: number,
): { x: number; y: number; width: number; height: number } {
  const wanted = fontSizePt * LINE_BOX_RATIO;
  if (!(wanted < box.height)) return { ...box };
  return { x: box.x, y: box.y + (box.height - wanted), width: box.width, height: wanted };
}

/** How many choices one dropdown may offer. A pick-one list longer than
 *  this isn't a form control, it's a data-entry mistake. */
export const ESIGN_MAX_DROPDOWN_OPTIONS = 40;
const MAX_OPTION_LENGTH = 120;
const MAX_GROUP_NAME_LENGTH = 60;

export function isEsignFieldType(v: unknown): v is EsignFieldType {
  return typeof v === "string" && (ESIGN_FIELD_TYPES as readonly string[]).includes(v);
}

/** BoldSign's fieldType enum value for each of ours (their casing —
 *  from the OpenAPI-generated SDK's FormField.FieldTypeEnum). */
export const BOLDSIGN_FIELD_TYPE: Record<EsignFieldType, string> = {
  signature: "Signature",
  initials: "Initial",
  date_signed: "DateSigned",
  text: "TextBox",
  checkbox: "CheckBox",
  dropdown: "DropDown",
  radio: "RadioButton",
  // Distinct from DateSigned: the signer PICKS this date (date of loss,
  // requested effective date), rather than it being stamped with the
  // moment they signed.
  editable_date: "EditableDate",
  // The signer uploads a file. What they upload is pulled back out of
  // the provider on completion and filed on the record beside the
  // signed document — see shared/db/esign-archive.ts.
  attachment: "Attachment",
};

/**
 * WHAT A CHECKBOX'S `value` MUST SAY TO ARRIVE TICKED.
 *
 * BoldSign carries a checkbox's state in the same `value` every other
 * field uses for its printed text, as the literal word "on" or "off".
 * Their SendDocument guide is explicit about it and their prefill API
 * uses the same pair, so this is their spelling rather than ours.
 *
 * Named constants because "on" as a bare string, sitting beside real
 * printed values like "Maricopa and Pinal County", reads like somebody's
 * typo. It is a protocol word.
 *
 * Both are stored when a default is decided. An unticked box and a box
 * nobody has decided about print the same, and only one of them is an
 * answer — the same distinction the offer-defaults table keeps between
 * a decided no and an unanswered question.
 */
export const BOLDSIGN_CHECKBOX_ON = "on";
export const BOLDSIGN_CHECKBOX_OFF = "off";

/** One placed field as the API accepts it from the prepare screen and
 *  as it is persisted (minus ids). Coordinates per the module header. */
export interface EsignPlacedField {
  /** Which signer on THIS send the field belongs to.
   *
   *  For a signer who is one of our contacts this IS their contact id,
   *  which is what it has always been and why saved layouts, existing
   *  envelopes and the older wire shape all keep working unchanged.
   *
   *  For a signer who is NOT one of our contacts — a co-op agent, a
   *  seller, a title officer (Grant, 2026-09-03) — it is a key the
   *  prep screen minted for the length of that screen's life, because
   *  such a person deliberately has no contact row to be identified by.
   *  Never persisted: what IS stored is the signer's ordinal position
   *  (saved layouts) or its row id (esign_envelope_fields.signer_id),
   *  so the key is a within-one-request handle and nothing more.
   *
   *  Named for what it is rather than `contactId`, because a field
   *  owner that is not always a contact must not be called one — a
   *  guest key sitting in a column called contactId is the kind of
   *  quiet lie that gets written to the wrong table later. */
  signerKey: string;
  /** THE SEAT A LOCKED BOX READS ITS PERSON FROM (Baker, 2026-09-16).
   *
   *  A locked box belongs to the preparer, so `signerKey` above is the
   *  preparer's sentinel. When such a box is bound to a person's field
   *  — the buyer's legal name, printed but not editable by them — this
   *  is the chair whose occupant it reads. See bindingPersonSignerKey.
   *
   *  Null on every other field: a signer's own box already names its
   *  person, and an ordinary locked box holds boilerplate that belongs
   *  to nobody. */
  roleKey?: string | null;
  fieldType: EsignFieldType;
  /** 1-based. */
  pageNumber: number;
  x: number;
  y: number;
  width: number;
  height: number;
  required: boolean;
  /** Static prefill (text fields only in this phase). On a BOUND field
   *  this doubles as the per-send override: set means "use this instead
   *  of resolving", with the binding left intact so the next send
   *  resolves normally (2026-08-23). */
  value?: string | null;
  /** Keeps a PREFILLED text field editable by the signer.
   *
   *  Opt-in, and rare. The default is that a value we filled in prints
   *  and cannot be changed (see printsAsReadOnly) — forgetting this flag
   *  protects a value rather than exposing one, which is the direction
   *  a default like this has to fail in. Set only where the sender means
   *  their text as a head start the signer finishes. */
  signerMayEdit?: boolean | null;
  /** Points — what a text value prints at.
   *
   *  Read off the caption beside the blank when the document was mapped,
   *  so a value lands at the size of the type around it instead of the
   *  provider's own web-form default (Baker, 2026-09-18). Absent on a
   *  hand-placed box and on every map saved before this existed; the
   *  send falls back to DEFAULT_PRINTED_FONT_PT rather than to the
   *  provider's choice. */
  fontSize?: number | null;
  /** How a LOCKED text box's type looks: bold, italic, underline, color,
   *  size and indent (Nate, 2026-10-26). Only the sender's own box prints
   *  this (the stamper draws it); on any other field it is dropped, because
   *  the signing service decides how a signer's text looks. See
   *  shared/esign/text-style.ts. */
  textStyle?: EsignTextStyle | null;
  /** A TEXT BOX THAT SPANS SEVERAL PRINTED LINES (Grant, 2026-09-29).
   *
   *  `multiline` says so; `linePitch` is the distance in points between
   *  the rules it sits on, and is the ONE number every derivation
   *  (line count, where each rule is, where each baseline goes) comes
   *  from — see shared/esign/multiline-text.ts. `firstLineIndent` is how
   *  far in from the box's left edge the first line starts, for a form
   *  that prints a caption on the first rule and runs full lines under
   *  it. All three absent on a single-line box and on every map saved
   *  before they existed. Text fields only. */
  multiline?: boolean | null;
  linePitch?: number | null;
  firstLineIndent?: number | null;
  /** A LINE DRAWN THROUGH PRINTED TEXT (toolbar program PR 4a, Grant,
   *  2026-10-14). The sender strikes a clause that does not apply before
   *  the package goes out. Not a field the signer sees: it is a sender-owned
   *  `text` box (preparer key) that prints a line instead of a value, so it
   *  rides the existing locked-box path into the PDF's bytes and never
   *  reaches the provider. Preparer text only; any other owner is refused.
   *  DECISION: a flag on a locked text box, not a tenth EsignFieldType,
   *  because the nine types are locked by a CHECK on two tables and a strike
   *  is never stored in either (it is per-send, not remembered in a layout). */
  strike?: boolean | null;
  /** The merge token this field pulls its value from, when it is bound
   *  to record data — the same catalog the automation emails use, so the
   *  sensitive-field denylist applies here identically. Text fields
   *  only; a signature can't be merged. */
  mergeToken?: string | null;
  /** Dropdown only: the choices offered, in the order they appear.
   *  Empty for every other type. */
  options?: readonly string[] | null;
  /** The caption this field was placed against, and how far from it.
   *
   *  Computed in the browser (anchorForBox), where the document's text
   *  layer already is, and carried straight through to the saved map —
   *  it is what lets the map survive the publisher reissuing the form
   *  (shared/esign/layout-carryover.ts). Never used for placement on
   *  THIS send: nothing here moves a field the advisor put somewhere.
   *
   *  Null for a scan, an older client, or a field with no caption near
   *  it. All three mean the same downstream: this field cannot be
   *  carried onto a future revision, and comes back unmapped rather
   *  than guessed. */
  anchor?: FieldAnchor | null;
  /** The atlas's fill rule for this blank, carried from the applied
   *  layout so the server can honour rules that are about THIS SEND
   *  rather than about the record (2026-09-18).
   *
   *  Exactly one such rule exists today: the purchase contract's twelve
   *  "Addenda Incorporated" boxes, each ticked when its addendum is in
   *  this package. The package is only known at send time, so the rule
   *  cannot be answered on the screen and cannot be baked into the map.
   *
   *  It grants the client nothing it did not have: a request that can
   *  send a crafted rule can already send `value` directly, and the
   *  rule's only effect is to OVERWRITE that value from the envelope's
   *  own contents. Null on every other field and every older client. */
  fillRule?: string | null;
  /** Radio only: which buttons make up ONE choice. Two buttons sharing
   *  a group name are mutually exclusive to the signer; a button with
   *  no group is a choice of one, which is a bug, so it's rejected. */
  groupName?: string | null;
}

/** Per-page size in the same coordinate space (pdf.js scale-1 viewport),
 *  used to reject fields that fall off the page — BoldSign refuses such
 *  a send with an opaque error, so we fail it loudly and namedly first. */
export interface PdfPageSize {
  pageNumber: number;
  width: number;
  height: number;
}

export type FieldValidationError =
  | { kind: "bad_type"; index: number }
  | { kind: "bad_page"; index: number; pageNumber: number }
  | { kind: "bad_geometry"; index: number }
  | { kind: "off_page"; index: number; pageNumber: number }
  | { kind: "unknown_signer"; index: number; signerKey: string }
  | { kind: "missing_options"; index: number }
  | { kind: "too_many_options"; index: number }
  | { kind: "missing_group"; index: number }
  | { kind: "unknown_seat"; index: number; roleKey: string }
  | { kind: "too_many_signer_fields"; index: number; count: number; limit: number }
  | { kind: "too_many_preparer_fields"; index: number; count: number; limit: number };

const MIN_FIELD_SIZE_PT = 8; // anything smaller is untappable and almost certainly a mis-drag

/**
 * TWO CEILINGS, BECAUSE THE TWO KINDS OF BOX COST DIFFERENT THINGS
 * (2026-09-18).
 *
 * There was one ceiling of 200, commented "far above any real document",
 * and the field atlas disproves that outright. Compiled from the
 * reviewed atlas, the AAR Residential Resale Purchase Contract is 225
 * fields and the Seller's Property Disclosure Statement is 548. Both
 * were refused before a single field was looked at, with the message
 * "One of the placed fields has an invalid size or position" — which is
 * not what happened, and sends whoever reads it hunting an eleven-page
 * contract for a box that is perfectly fine.
 *
 * The two kinds are not the same resource:
 *
 *   A SIGNER's field becomes a field on the provider's envelope. It
 *   costs an API payload and a thing the signer is asked to do, and
 *   wanting hundreds of them on one document means something is wrong.
 *   200 is genuinely far above any real document HERE: the purchase
 *   contract has 72 and the disclosure statement has 24.
 *
 *   A PREPARER's box is drawn into the PDF's own bytes before anything
 *   is sent (document-stamping.ts) and then dropped by sendForSignature,
 *   so it never reaches the provider at all. It costs one pdf-lib
 *   drawText. That is why a form can legitimately carry hundreds: the
 *   purchase contract has 153 and the disclosure statement 524, and
 *   every one is a value the firm prints rather than a question the
 *   client answers. Counting those against a provider limit was the bug.
 *
 * 1000 for preparer boxes: above the largest real form by a comfortable
 * margin, and still a bound on a runaway client.
 */
const MAX_SIGNER_FIELDS_PER_SEND = 200;
const MAX_PREPARER_FIELDS_PER_SEND = 1000;

export const ESIGN_MAX_SIGNER_FIELDS_PER_SEND = MAX_SIGNER_FIELDS_PER_SEND;
export const ESIGN_MAX_PREPARER_FIELDS_PER_SEND = MAX_PREPARER_FIELDS_PER_SEND;

/** @deprecated The single ceiling this replaced. Kept as the signer
 *  limit so an older import keeps compiling and means what it used to. */
export const ESIGN_MAX_FIELDS_PER_SEND = MAX_SIGNER_FIELDS_PER_SEND;

/**
 * An anchor off the wire, or null.
 *
 * ALL of text/page/occurrence/dx/dy must be present and sane. A partial
 * anchor is not a weaker one — it is an anchor that would later carry a
 * field to a position computed from a missing number, which lands a
 * signature box in the corner of a contract looking every bit as
 * deliberate as the ones that are right. Half an anchor is treated as
 * none: the field simply can't be carried onto a future revision.
 *
 * Never rejects the whole request. An advisor's placement is real work,
 * and refusing to save forty correct fields because one anchor arrived
 * malformed would trade the thing that matters for the thing that helps
 * next year.
 */
function parseFieldAnchor(raw: unknown): FieldAnchor | null {
  if (!raw || typeof raw !== "object") return null;
  const a = raw as Record<string, unknown>;
  const text = typeof a.text === "string" ? a.text.trim().slice(0, 200) : "";
  const pageNumber = Number(a.pageNumber);
  const occurrence = Number(a.occurrence);
  const dx = Number(a.dx);
  const dy = Number(a.dy);
  if (!text) return null;
  if (!Number.isInteger(pageNumber) || pageNumber < 1) return null;
  if (!Number.isInteger(occurrence) || occurrence < 0) return null;
  if (!Number.isFinite(dx) || !Number.isFinite(dy)) return null;
  return {
    text,
    pageNumber,
    occurrence,
    dx,
    dy,
    token: typeof a.token === "string" && a.token ? a.token.slice(0, 120) : null,
  };
}

/** Parses + validates the raw `fields` array from a send request body.
 *  Returns the clean list or the FIRST error (the UI validates as the
 *  user works, so a server-side failure here is a bug or a stale
 *  client — one precise error beats a partial list). */
export function parseEsignFields(
  raw: unknown,
  options: {
    pageSizes: readonly PdfPageSize[];
    /** Every signer key this send actually has. A field owned by
     *  anything else is refused — the prep screen picks owners from
     *  this same list, so an unknown one means a stale client or a
     *  hand-made request, never a legitimate send. */
    eligibleSignerKeys: ReadonlySet<string>;
  },
): { ok: true; fields: EsignPlacedField[] } | { ok: false; error: FieldValidationError } {
  if (!Array.isArray(raw)) return { ok: true, fields: [] };
  // Counted per kind, AFTER reading each row's owner — the two ceilings
  // above are different resources and one number could not express them.
  // Deliberately before the per-field walk so a document with a
  // thousand boxes is refused cheaply rather than validated first.
  let signerCount = 0;
  let preparerCount = 0;
  for (const f of raw) {
    const key = (f as { signerKey?: unknown; contactId?: unknown } | null)?.signerKey;
    if (key === PREPARER_SIGNER_KEY) preparerCount += 1;
    else signerCount += 1;
  }
  if (signerCount > MAX_SIGNER_FIELDS_PER_SEND) {
    return {
      ok: false,
      error: {
        kind: "too_many_signer_fields",
        index: MAX_SIGNER_FIELDS_PER_SEND,
        count: signerCount,
        limit: MAX_SIGNER_FIELDS_PER_SEND,
      },
    };
  }
  if (preparerCount > MAX_PREPARER_FIELDS_PER_SEND) {
    return {
      ok: false,
      error: {
        kind: "too_many_preparer_fields",
        index: MAX_PREPARER_FIELDS_PER_SEND,
        count: preparerCount,
        limit: MAX_PREPARER_FIELDS_PER_SEND,
      },
    };
  }
  const byPage = new Map(options.pageSizes.map((p) => [p.pageNumber, p]));
  const fields: EsignPlacedField[] = [];
  for (let i = 0; i < raw.length; i++) {
    const f = raw[i] as Record<string, unknown>;
    if (!f || typeof f !== "object")
      return { ok: false, error: { kind: "bad_geometry", index: i } };
    if (!isEsignFieldType(f.fieldType)) return { ok: false, error: { kind: "bad_type", index: i } };
    // `contactId` is the pre-2026-09-03 wire name for the same thing.
    // Accepted because the Worker and the web bundle deploy as separate
    // Cloudflare resources: a prep screen a sender already has open
    // keeps sending the old key after the new Worker goes live, and
    // dropping it would fail a send mid-preparation for no gain. Same
    // reasoning as the /transactions/:id/send route alias.
    const signerKey =
      typeof f.signerKey === "string" && f.signerKey.length > 0
        ? f.signerKey
        : typeof f.contactId === "string"
          ? f.contactId
          : "";
    // The preparer's own box belongs to nobody on the send, so it is
    // deliberately exempt from the eligible-signer test — and confined
    // to the types that make sense locked.
    if (signerKey === PREPARER_SIGNER_KEY) {
      if (!PREPARER_FIELD_TYPES.includes(f.fieldType)) {
        return { ok: false, error: { kind: "bad_type", index: i } };
      }
      // A strike is a locked TEXT box that prints a line, nothing else.
      if (f.strike === true && f.fieldType !== "text") {
        return { ok: false, error: { kind: "bad_type", index: i } };
      }
    } else if (f.strike === true) {
      // A signer cannot be handed a strike: it would reach the provider as
      // an empty text box the signer is asked to fill.
      return { ok: false, error: { kind: "bad_type", index: i } };
    } else if (!options.eligibleSignerKeys.has(signerKey)) {
      return { ok: false, error: { kind: "unknown_signer", index: i, signerKey } };
    }
    const pageNumber = typeof f.pageNumber === "number" ? f.pageNumber : NaN;
    const x = typeof f.x === "number" ? f.x : NaN;
    const y = typeof f.y === "number" ? f.y : NaN;
    const width = typeof f.width === "number" ? f.width : NaN;
    const height = typeof f.height === "number" ? f.height : NaN;
    if (
      !Number.isFinite(x) ||
      !Number.isFinite(y) ||
      !Number.isFinite(width) ||
      !Number.isFinite(height)
    ) {
      return { ok: false, error: { kind: "bad_geometry", index: i } };
    }
    if (width < MIN_FIELD_SIZE_PT || height < MIN_FIELD_SIZE_PT) {
      return { ok: false, error: { kind: "bad_geometry", index: i } };
    }
    if (!Number.isInteger(pageNumber) || pageNumber < 1) {
      return { ok: false, error: { kind: "bad_page", index: i, pageNumber } };
    }
    const page = byPage.get(pageNumber);
    // When the caller supplied page sizes, enforce on-page bounds; a
    // caller with no geometry (tests, non-UI callers) skips the check.
    if (options.pageSizes.length > 0) {
      if (!page) return { ok: false, error: { kind: "bad_page", index: i, pageNumber } };
      if (x < 0 || y < 0 || x + width > page.width || y + height > page.height) {
        return { ok: false, error: { kind: "off_page", index: i, pageNumber } };
      }
    }
    // A dropdown with no choices renders to the signer as an empty
    // control they cannot satisfy, and a required one deadlocks the
    // whole envelope. Same for a radio button with no group: the
    // provider treats each ungrouped button as its own yes/no, so
    // "I elect / I decline" would accept BOTH. Both are refused here
    // rather than sent and discovered by a client.
    let dropdownOptions: string[] | null = null;
    let groupName: string | null = null;
    if (f.fieldType === "dropdown") {
      const raw = Array.isArray(f.options) ? f.options : [];
      const cleaned = raw
        .filter((o): o is string => typeof o === "string")
        .map((o) => o.trim().slice(0, MAX_OPTION_LENGTH))
        .filter((o) => o.length > 0);
      if (cleaned.length === 0) return { ok: false, error: { kind: "missing_options", index: i } };
      if (cleaned.length > ESIGN_MAX_DROPDOWN_OPTIONS) {
        return { ok: false, error: { kind: "too_many_options", index: i } };
      }
      dropdownOptions = cleaned;
    } else if (f.fieldType === "radio") {
      const g =
        typeof f.groupName === "string" ? f.groupName.trim().slice(0, MAX_GROUP_NAME_LENGTH) : "";
      if (g.length === 0) return { ok: false, error: { kind: "missing_group", index: i } };
      groupName = g;
    }
    // The seat a LOCKED box reads its person from. Validated against the
    // catalog so a stale or hand-made request cannot invent one: an
    // unknown seat resolves to nobody, and the resolver reports the
    // field missing and stops the send — but a seat that is simply
    // mistyped should be refused here, where the error names the field,
    // rather than surfacing later as "this box has no value".
    const roleKey =
      typeof f.roleKey === "string" && f.roleKey.trim().length > 0 ? f.roleKey.trim() : null;
    if (roleKey && !isKnownRoleKey(roleKey)) {
      return { ok: false, error: { kind: "unknown_seat", index: i, roleKey } };
    }
    fields.push({
      signerKey,
      ...(roleKey ? { roleKey } : {}),
      fieldType: f.fieldType,
      pageNumber,
      x,
      y,
      width,
      height,
      required: f.required !== false,
      options: dropdownOptions,
      groupName,
      value: typeof f.value === "string" && f.value.length > 0 ? f.value.slice(0, 2000) : null,
      // Opt-in only: anything that is not literally `true` leaves the
      // prefill protected. A stale client sends neither key and gets
      // the protected answer, which is the safe direction to be wrong.
      signerMayEdit: f.signerMayEdit === true,
      // Bounded rather than trusted: a font size is a number a request
      // can name freely, and one outside human type sizes would print a
      // client's contract unreadable or a single character across the
      // page. Out of range is dropped to the default, never clamped
      // silently into something the screen never showed.
      fontSize:
        typeof f.fontSize === "number" &&
        Number.isFinite(f.fontSize) &&
        f.fontSize >= MIN_FIELD_FONT_PT &&
        f.fontSize <= MAX_FIELD_FONT_PT
          ? f.fontSize
          : null,
      // A multi-line box is only ever a text box, and only counts as one
      // with a usable pitch: the flag without the number would be a
      // single-line box the screen and the stamper disagree about.
      // Bounded the same way the font size is, and for the same reason.
      ...multilineFacts(f),
      // Style is the sender's own box only; elsewhere it would be stored
      // and silently ignored, so it is dropped here.
      ...(signerKey === PREPARER_SIGNER_KEY && f.fieldType === "text" && parseTextStyle(f.textStyle)
        ? { textStyle: parseTextStyle(f.textStyle) }
        : {}),
      ...(signerKey === PREPARER_SIGNER_KEY && f.fieldType === "text" && f.strike === true
        ? { strike: true }
        : {}),
      // A binding is only meaningful on a text field — nothing else has
      // a value to fill — so it is dropped elsewhere rather than stored
      // and silently ignored.
      mergeToken:
        f.fieldType === "text" &&
        typeof f.mergeToken === "string" &&
        f.mergeToken.length > 0
          ? f.mergeToken.slice(0, 120)
          : null,
      anchor: parseFieldAnchor(f.anchor),
      fillRule:
        typeof f.fillRule === "string" && f.fillRule.length > 0
          ? f.fillRule.slice(0, 400)
          : null,
    });
  }
  return { ok: true, fields };
}

// ─── Advisor self-signing stamp placements ─────────────────────────
//
// A stamp is NOT a signer field — it has no fieldType, no signer, and
// never travels to BoldSign as a `formField`. It's a placement the OS
// itself draws into the PDF's bytes before the send, via
// shared/db/esign-signatures.ts applyEsignStamps(). Same coordinate
// space and the same off-page discipline as parseEsignFields, kept as
// a sibling parser rather than folded into it because the two error
// unions and eligibility rules (a stamp has no "signer" to validate)
// are genuinely different shapes.

export interface StampPlacementInput {
  pageNumber: number;
  x: number;
  y: number;
  width: number;
  height: number;
}

export type StampValidationError =
  | { kind: "bad_page"; index: number }
  | { kind: "bad_geometry"; index: number }
  | { kind: "off_page"; index: number };

const MAX_STAMPS_PER_SEND = 20; // an advisor stamps a handful of spots, not hundreds

export function parseStampPlacements(
  raw: unknown,
  options: { pageSizes: readonly PdfPageSize[] },
): { ok: true; stamps: StampPlacementInput[] } | { ok: false; error: StampValidationError } {
  if (!Array.isArray(raw) || raw.length === 0) return { ok: true, stamps: [] };
  if (raw.length > MAX_STAMPS_PER_SEND) {
    return { ok: false, error: { kind: "bad_geometry", index: MAX_STAMPS_PER_SEND } };
  }
  const byPage = new Map(options.pageSizes.map((p) => [p.pageNumber, p]));
  const stamps: StampPlacementInput[] = [];
  for (let i = 0; i < raw.length; i++) {
    const s = raw[i] as Record<string, unknown>;
    const pageNumber = typeof s?.pageNumber === "number" ? s.pageNumber : NaN;
    const x = typeof s?.x === "number" ? s.x : NaN;
    const y = typeof s?.y === "number" ? s.y : NaN;
    const width = typeof s?.width === "number" ? s.width : NaN;
    const height = typeof s?.height === "number" ? s.height : NaN;
    if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(width) || !Number.isFinite(height)) {
      return { ok: false, error: { kind: "bad_geometry", index: i } };
    }
    if (width < MIN_FIELD_SIZE_PT || height < MIN_FIELD_SIZE_PT) {
      return { ok: false, error: { kind: "bad_geometry", index: i } };
    }
    if (!Number.isInteger(pageNumber) || pageNumber < 1) {
      return { ok: false, error: { kind: "bad_page", index: i } };
    }
    if (options.pageSizes.length > 0) {
      const page = byPage.get(pageNumber);
      if (!page) return { ok: false, error: { kind: "bad_page", index: i } };
      if (x < 0 || y < 0 || x + width > page.width || y + height > page.height) {
        return { ok: false, error: { kind: "off_page", index: i } };
      }
    }
    stamps.push({ pageNumber, x, y, width, height });
  }
  return { ok: true, stamps };
}

export function stampValidationMessage(error: StampValidationError): string {
  switch (error.kind) {
    case "bad_page":
      return "Your signature stamp points at a page this document doesn't have.";
    case "bad_geometry":
      return "Your signature stamp has an invalid size or position.";
    case "off_page":
      return "Your signature stamp falls outside the page — move it fully onto the page and try again.";
  }
}

export function fieldValidationMessage(error: FieldValidationError): string {
  switch (error.kind) {
    case "bad_type":
      return "One of the placed fields has an unknown type.";
    case "bad_page":
      return `A field points at page ${error.pageNumber}, which this document doesn't have.`;
    case "bad_geometry":
      return "One of the placed fields has an invalid size or position.";
    // SAYS WHAT HAPPENED. Both of these used to come out as the geometry
    // message above, which sent the reader hunting an eleven-page
    // contract for a box that was perfectly fine.
    case "too_many_signer_fields":
      return (
        `This document asks the signers to fill ${error.count} fields, and the limit is ` +
        `${error.limit}. Remove some, or split it into two sends.`
      );
    case "too_many_preparer_fields":
      return (
        `This document has ${error.count} boxes you fill before sending, and the limit is ` +
        `${error.limit}. That is far more than any real form — check the field map.`
      );
    case "off_page":
      // Names the page. "One of the placed fields" meant hunting an
      // 11-page contract for the offending box (staging round 3,
      // 2026-08-23). The UI blocks this before Send, so reaching here
      // means a stale client — which is exactly when a precise message
      // matters most.
      return `A field on page ${error.pageNumber} falls outside the page — move it fully onto the page and try again.`;
    case "unknown_signer":
      return "One of the placed fields is assigned to someone who isn't a signer on this send.";
    case "missing_options":
      return "A pick-one list has no choices in it. Add the options, or delete the field.";
    case "too_many_options":
      return `A pick-one list has more than ${ESIGN_MAX_DROPDOWN_OPTIONS} choices. Trim it down.`;
    case "missing_group":
      return "A choose-one button isn't part of a group. Give it a group name so the buttons in that group rule each other out.";
    case "unknown_seat":
      // A locked box naming a seat that is not on this form's side, or
      // a seat key that no longer exists. Refused here, where the
      // message can name it, rather than resolving to nobody later and
      // reading as "this box has no value".
      return `A locked field is set to read "${error.roleKey}", which isn't a role on this document.`;
  }
}
